import { Component } from "@theme/component";

/**
 * A custom element that renders a video background.
 *
 * Sources arrive with their URL in `data-video-source` rather than `src`, so the parser
 * fetches nothing. We promote them to real `src` attributes only once the element is
 * actually displayed and near the viewport.
 *
 * That gating matters because background-media.liquid renders a desktop *and* a mobile
 * variant on every page and hides one with `display: none`. A hidden element never
 * intersects, so only the variant in use downloads its video instead of both.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ["videoSources", "videoElement"];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadSources();
      },
      // Start fetching just before the video scrolls into view.
      { rootMargin: "200px" }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
  }

  /**
   * Promotes the deferred `data-video-source` URLs to `src` and starts loading.
   */
  #loadSources() {
    // Only ever load once; the observer has done its job.
    this.#observer?.disconnect();
    this.#observer = undefined;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute("src", videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get("video-background-component")) {
  customElements.define("video-background-component", VideoBackgroundComponent);
}
